import * as React from "react";
import {
  Paper,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Chip,
  Stack,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Divider,
  Autocomplete,
  TextField,
  Box,
} from "@mui/material";
import { useTheme, useMediaQuery } from "@mui/material";
import {
  EquipmentsRecord,
  RemovalRecord,
} from "../../interfaces/Equipment.interface";
import { formatEquipmentLabel } from "../../utils/formatEquipment";
import { RemovalPassedFromLabels } from "../../common/consts/RemovalPassedFrom.const";

const API_URL =
  process.env.REACT_APP_API_URL || "https://trackency.hubency.com/api";

interface RemovalListResponse {
  data: { data: RemovalRecord[]; total: number };
}

export const RemovalsList: React.FC<{
  siteId: string;
  inviteToken: string;
  equipments: EquipmentsRecord[];
}> = ({ siteId, equipments, inviteToken }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [rows, setRows] = React.useState<RemovalRecord[]>([]);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  const [selectedEquipments, setSelectedEquipments] = React.useState<
    EquipmentsRecord[]
  >([]);

  const equipmentOptions = React.useMemo(
    () =>
      equipments.map((e) => ({
        id: e._id!,
        label: formatEquipmentLabel(e),
      })),
    [equipments]
  );

  const selectedIds = React.useMemo(
    () => selectedEquipments.map((e) => e._id!).filter(Boolean),
    [selectedEquipments]
  );

  const load = React.useCallback(async () => {
    const p = page + 1;
    const qp = new URLSearchParams({
      inviteToken,
      siteId,
      page: String(p),
      limit: String(rowsPerPage),
    });
    if (selectedIds.length) {
      qp.set("equipmentIds", selectedIds.join(","));
    }

    const res = await fetch(
      `${API_URL}/site-inventory/invite/list/${siteId}?${qp.toString()}`
    );
    if (!res.ok) throw new Error("Impossible de charger les déclarations");
    const json = (await res.json()) as RemovalListResponse;
    setRows(json.data.data);
    setTotal(json.data.total);
  }, [inviteToken, siteId, page, rowsPerPage, selectedIds]);

  React.useEffect(() => {
    load().catch(() => {
      setRows([]);
      setTotal(0);
    });
  }, [load]);

  const fmtDate = (d: string) =>
    new Date(d).toLocaleString(undefined, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });

  const TableView = () => (
    <>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Date</TableCell>
            <TableCell>Équipement</TableCell>
            <TableCell align="center">Vide(s)</TableCell>
            <TableCell align="center">25%</TableCell>
            <TableCell align="center">50%</TableCell>
            <TableCell align="center">75%</TableCell>
            <TableCell align="center">100%</TableCell>
            <TableCell align="center">125%</TableCell>
            <TableCell align="center">Équiv. plein</TableCell>
            <TableCell align="center">Équiv. tonnage</TableCell>
            <TableCell>Ajouter Par</TableCell>
            <TableCell>Note</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.id} hover>
              <TableCell>{fmtDate(r.createdAt)}</TableCell>
              <TableCell>{formatEquipmentLabel(r.equipmentData)}</TableCell>
              <TableCell align="center">{r.counts._0 ?? 0}</TableCell>
              <TableCell align="center">{r.counts._25 ?? 0}</TableCell>
              <TableCell align="center">{r.counts._50 ?? 0}</TableCell>
              <TableCell align="center">{r.counts._75 ?? 0}</TableCell>
              <TableCell align="center">{r.counts._100 ?? 0}</TableCell>
              <TableCell align="center">{r.counts._125 ?? 0}</TableCell>
              <TableCell align="center">
                <Chip
                  label={`${r.equivalentFullUnits.toFixed(2)} / ${
                    r.quantityAtTheMoment
                  }`}
                  size="small"
                  color="primary"
                />
              </TableCell>
              <TableCell>
                {r.tonnage ? `${r.tonnage?.toFixed(4)} t` : "N/A"}
              </TableCell>
              <TableCell align="center">
                {RemovalPassedFromLabels[r.passedFrom] ?? r.passedFrom ?? "N/A"}
              </TableCell>
              <TableCell
                sx={{
                  maxWidth: 200, // largeur max pour la colonne note
                  whiteSpace: "nowrap", // une seule ligne
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                title={r.note || ""}
              >
                {r.note || "-"}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setPage(0);
          setRowsPerPage(parseInt(e.target.value, 10));
        }}
        rowsPerPageOptions={[5, 10, 25, 50]}
      />
    </>
  );

  const CardsView = () => (
    <Stack spacing={2}>
      {rows.map((r) => (
        <Card key={r.id} variant="outlined">
          <CardHeader
            title={formatEquipmentLabel(r.equipmentData)}
            subheader={fmtDate(r.createdAt)}
          />
          <CardContent>
            <Grid container spacing={1}>
              <Grid size={{ xs: 6 }}>
                <Chip
                  label={`Vides: ${r.counts._0 ?? 0}`}
                  size="small"
                  variant="outlined"
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Chip
                  label={`25%: ${r.counts._25 ?? 0}`}
                  color="error"
                  size="small"
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Chip
                  label={`50%: ${r.counts._50 ?? 0}`}
                  color="warning"
                  size="small"
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Chip
                  label={`75%: ${r.counts._75 ?? 0}`}
                  color="success"
                  size="small"
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Chip
                  label={`100%: ${r.counts._100 ?? 0}`}
                  color="success"
                  size="small"
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Chip
                  label={`125%: ${r.counts._125 ?? 0}`}
                  color="error"
                  size="small"
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Divider sx={{ my: 1 }} />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Chip
                  label={`${r.equivalentFullUnits.toFixed(2)} / ${
                    r.quantityAtTheMoment
                  }`}
                  color="primary"
                  size="small"
                />
              </Grid>
              {r.note && (
                <Grid size={{ xs: 12 }}>
                  <Typography variant="body2" color="text.secondary">
                    Note :
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      display: "-webkit-box",
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                    title={r.note}
                  >
                    {r.note}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      ))}
      <Stack direction="row" justifyContent="space-between">
        <Chip
          label="Précédent"
          onClick={() => setPage((p) => Math.max(0, p - 1))}
          variant="outlined"
        />
        <Chip
          label="Suivant"
          onClick={() => {
            const maxPage = Math.max(0, Math.ceil(total / rowsPerPage) - 1);
            setPage((p) => Math.min(maxPage, p + 1));
          }}
          variant="outlined"
        />
      </Stack>
    </Stack>
  );

  return (
    <Paper sx={{ p: 1, mt: 2 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        justifyContent="space-between"
        sx={{ p: 2, mt: 2 }}
      >
        <Box sx={{ minWidth: 260 }}>
          <Autocomplete
            multiple
            size="small"
            options={equipmentOptions}
            value={equipmentOptions.filter((o) => selectedIds.includes(o.id))}
            onChange={(_, newValues) => {
              const next = equipments.filter((eq) =>
                newValues.some((v) => v.id === eq._id)
              );
              setSelectedEquipments(next);
              setPage(0);
            }}
            getOptionLabel={(o) => o.label}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Filtrer par équipement(s)"
                placeholder="Tous"
              />
            )}
            clearOnBlur
          />
        </Box>
      </Stack>

      {isMobile ? <CardsView /> : <TableView />}
    </Paper>
  );
};
